import { XIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Image } from '@/components/Image';
import { CircularProgress } from '@/components/CircularProgress/CircularProgress';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { useQueryPanelStore } from '@/stores';

interface QueryAttachmentRowProps {
    imageSrc: string;
    fileName?: string;
    isUploading: boolean;
    uploadProgress: number;
    onRemove: () => void;
}

export function QueryAttachmentRow({
    imageSrc,
    fileName,
    isUploading,
    uploadProgress,
    onRemove,
}: QueryAttachmentRowProps) {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const imageUsesAuth = !imageSrc.startsWith('blob:');
    const snapshotHidden = useQueryPanelStore(
        (state) => state.attachmentSnapshotHidden,
    );

    return (
        <div
            className={cn(
                'px-[14px] pb-3 pt-2',
                snapshotHidden
                    ? 'pointer-events-none opacity-0'
                    : 'opacity-100 transition-opacity duration-200 ease-out',
            )}
        >
            <div
                data-query-panel-attachment-anchor
                className="group relative h-16 w-fit overflow-hidden rounded-[5px]"
            >
                <Image
                    src={imageSrc}
                    alt={fileName ?? t('attachment.defaultAlt')}
                    auth={imageUsesAuth}
                    className="h-full w-auto object-contain"
                />
                {isUploading ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/45">
                        <CircularProgress progress={uploadProgress} size={6} />
                    </div>
                ) : (
                    <>
                        <div className="absolute inset-0 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100" />
                        <button
                            type="button"
                            onClick={onRemove}
                            className="absolute right-1 top-1 z-20 flex size-4 items-center justify-center rounded text-white-100 opacity-0 transition-opacity group-hover:opacity-100 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-grey-400"
                            aria-label={t('attachment.removeLabel')}
                        >
                            <XIcon className="size-3" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
